import { useCallback } from 'react';
import { ActionType, ComponentType } from '@cbhq/client-analytics';
import { Target } from '@cbhq/identity-shared/types';

import { useClientProps } from '../providers/ClientPropsProvider';

import { useAnalytics } from './useAnalytics';

export const mapTargetToUrl: Record<Target, string> = {
  production: 'https://login.coinbase.com',
  staging: 'https://login-staging.cbhq.net',
  development: 'https://login-dev.cbhq.net',
  local: 'http://localhost:3000',
  mock: 'http://localhost:3000',
};

const CASTLE_TOKEN_TIMEOUT_MS = 500;

async function getCastleToken(): Promise<string | undefined> {
  try {
    const Castle = (await import('@castleio/castle-js')).default;
    return await Promise.race([
      Castle.createRequestToken(),
      new Promise<undefined>((resolve) => {
        setTimeout(() => {
          resolve(undefined);
        }, CASTLE_TOKEN_TIMEOUT_MS);
      }),
    ]);
  } catch {
    return undefined;
  }
}

export function useSessionDelete() {
  const { target } = useClientProps();
  const { logEvent } = useAnalytics();
  return useCallback(async () => {
    try {
      const castleToken = await getCastleToken();

      await fetch(`${mapTargetToUrl[target]}/api/v1/delete`, {
        method: 'POST',
        cache: 'no-cache',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(castleToken && {
            'fingerprint-tokens': JSON.stringify({ castle: castleToken }),
          }),
        },
        body: JSON.stringify(''),
      });
    } catch (e) {
      logEvent('profile_menu_delete_session_error', {
        action: ActionType.process,
        componentType: ComponentType.dropdown,
        errorMessage: JSON.stringify(e),
      });
    }
  }, [logEvent, target]);
}

export function useGetUserIdFromSession() {
  const { config } = useClientProps();
  const { logEvent } = useAnalytics();
  return useCallback(async () => {
    if (config?.usmUserUuid) return config.usmUserUuid;

    try {
      const response = await fetch('/session');
      return ((await response.json())?.user_uuid ?? '') as string;
    } catch (e: unknown) {
      logEvent('profile_menu_get_session_error', {
        action: ActionType.process,
        componentType: ComponentType.dropdown,
        errorMessage: JSON.stringify(e),
      });
      return '';
    }
  }, [config?.usmUserUuid, logEvent]);
}
